import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ExpenseForm } from "@/pages/ExpensesPage";
import { Receipt, Paperclip, Pencil, Trash2, RotateCcw } from "lucide-react";

const STATUS = {
  approved: ["Approved", "bg-emerald-50 text-emerald-700"],
  declined: ["Declined", "bg-rose-50 text-rose-700"],
  submitted: ["Pending", "bg-amber-50 text-amber-700"],
  reimbursed: ["Reimbursed", "bg-indigo-50 text-indigo-700"],
};
const money = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const OPEN = new Set(["submitted", "declined"]);

function StatusNote({ e }) {
  const st = e.status || "submitted";
  if (st === "reimbursed") { return <div className="text-[11px] text-slate-400">by {e.reimbursed_by} · {day(e.reimbursed_at)}</div>; }
  if (e.reviewed_by && st !== "submitted") { return <div className="text-[11px] text-slate-400" title={e.review_note || ""}>by {e.reviewed_by}{e.review_note ? ` · “${e.review_note}”` : ""}</div>; }
  return null;
}

function Row({ e, onEdit, onDel, onResubmit }) {
  const st = e.status || "submitted";
  const [label, cls] = STATUS[st] || STATUS.submitted;
  return (
    <tr className="border-t border-slate-100" data-testid={`my-expense-${e.expense_id}`}>
      <td className="py-2.5 pr-4 whitespace-nowrap">{day(e.dated || e.created_at)}</td>
      <td className="py-2.5 pr-4">{e.category || "—"}</td>
      <td className="py-2.5 pr-4 max-w-[260px] truncate">{e.description}{e.receipt_file && <Paperclip className="inline w-3.5 h-3.5 ml-1 text-slate-400" />}</td>
      <td className="py-2.5 pr-4 font-medium whitespace-nowrap">{money(e.total_value)}</td>
      <td className="py-2.5 pr-4"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`} data-testid={`my-expense-status-${e.expense_id}`}>{label}</span><StatusNote e={e} /></td>
      <td className="py-2.5 text-right whitespace-nowrap">
        {OPEN.has(st) && <button onClick={() => onEdit(e)} className="text-slate-300 hover:text-indigo-600 mr-2" title="Edit" data-testid={`my-expense-edit-${e.expense_id}`}><Pencil className="w-4 h-4" /></button>}
        {st === "declined" && <button onClick={() => onResubmit(e)} className="text-slate-300 hover:text-amber-600 mr-2" title="Resubmit" data-testid={`my-expense-resubmit-${e.expense_id}`}><RotateCcw className="w-4 h-4" /></button>}
        {OPEN.has(st) && <button onClick={() => onDel(e)} className="text-slate-300 hover:text-rose-600" title="Delete" data-testid={`my-expense-del-${e.expense_id}`}><Trash2 className="w-4 h-4" /></button>}
      </td>
    </tr>
  );
}

export function MyExpenses({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => { api.get("/expenses/mine").then((r) => setRows(r.data.expenses)).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (err) { toast.error(err.response?.data?.detail || "Something went wrong"); } };
  const onDel = (e) => { if (window.confirm("Delete this expense claim?")) { act(() => api.delete(`/expenses/${e.expense_id}`), "Claim deleted"); } };
  const onResubmit = (e) => act(() => api.post(`/expenses/${e.expense_id}/status`, { status: "submitted" }), "Claim resubmitted for approval");
  const pending = (rows || []).filter((e) => (e.status || "submitted") === "submitted").reduce((s, e) => s + (e.total_value || 0), 0);
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 mt-6" data-testid="my-expenses">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-slate-900 flex items-center gap-2"><Receipt className="w-4 h-4 text-emerald-500" /> My Expenses</h2>
        {rows?.length > 0 && <span className="text-xs text-slate-500" data-testid="my-expenses-pending">{money(pending)} awaiting approval</span>}
      </div>
      {rows === null ? <p className="text-sm text-slate-400">Loading…</p> : !rows.length ? (
        <p className="text-sm text-slate-400 text-center py-6 bg-slate-50/60 rounded-lg border border-dashed border-slate-200" data-testid="my-expenses-empty">You haven't submitted any expenses yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500"><tr>{["Date", "Category", "Description", "Amount", "Status", ""].map((h) => <th key={h} className="py-2 pr-4">{h}</th>)}</tr></thead>
            <tbody>{rows.map((e) => <Row key={e.expense_id} e={e} onEdit={setEdit} onDel={onDel} onResubmit={onResubmit} />)}</tbody>
          </table>
        </div>
      )}
      {edit && <ExpenseForm expense={edit} onClose={() => setEdit(null)} onSaved={load} />}
    </section>
  );
}
